import { useEffect, useMemo, useState, FormEvent } from "react"
import apiClient from "../../lib/api"
import type { ISermon } from "@dc-ireland/shared"
import AdminAlerts from "../../components/admin/AdminAlerts"

const slugify = (value: string) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")

const isComplete = (sermon: ISermon) => Boolean(sermon.title.trim() && sermon.embedUrl.trim())

const isPartial = (sermon: ISermon) => {
  const hasTitle = Boolean(sermon.title.trim())
  const hasUrl = Boolean(sermon.embedUrl.trim())
  return hasTitle !== hasUrl
}

const buildPayload = (sermons: ISermon[]) =>
  sermons.filter(isComplete).map((sermon, index) => ({
    ...sermon,
    title: sermon.title.trim(),
    embedUrl: sermon.embedUrl.trim(),
    slug: sermon.slug?.trim() || slugify(sermon.title) || `sermon-${index}`,
    sortOrder: index,
    enabled: sermon.enabled !== false,
  }))

const serializePayload = (sermons: ISermon[]) =>
  JSON.stringify(
    buildPayload(sermons).map(({ slug, title, embedUrl, sortOrder, enabled }) => ({
      slug,
      title,
      embedUrl,
      sortOrder,
      enabled,
    }))
  )

const AdminSermons = () => {
  const [sermons, setSermons] = useState<ISermon[]>([])
  const [savedSermons, setSavedSermons] = useState<ISermon[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const isDirty = useMemo(
    () => serializePayload(sermons) !== serializePayload(savedSermons),
    [sermons, savedSermons]
  )

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true)
        const data = await apiClient.getSermons(true)
        const sorted = data.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
        setSermons(sorted)
        setSavedSermons(sorted)
      } catch (err) {
        console.error(err)
        setError("Failed to load sermons")
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  const updateSermon = (index: number, patch: Partial<ISermon>) => {
    setSermons((current) =>
      current.map((sermon, sermonIndex) => (sermonIndex === index ? { ...sermon, ...patch } : sermon))
    )
  }

  const addSermon = () => {
    setSermons((current) => [
      ...current,
      {
        slug: `sermon-${Date.now()}`,
        title: "",
        embedUrl: "",
        enabled: true,
      },
    ])
  }

  const removeSermon = (index: number) => {
    setSermons((current) => current.filter((_, sermonIndex) => sermonIndex !== index))
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setSuccess(null)

    if (sermons.some(isPartial)) {
      setError("Each sermon needs both a title and a YouTube embed URL, or remove the empty row.")
      return
    }

    const payload = buildPayload(sermons)

    if (!isDirty) {
      setSuccess("Already up to date.")
      return
    }

    if (payload.length === 0 && savedSermons.length === 0) {
      return
    }

    setSaving(true)
    try {
      const updated = await apiClient.updateSermons(payload)
      const sorted = updated.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
      setSermons(sorted)
      setSavedSermons(sorted)
      setSuccess("Sermons updated.")
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update sermons")
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return <span className="loading loading-spinner loading-md"></span>
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">Sermons</h2>
        <p className="mt-1 text-secondary">
          Manage YouTube embeds shown in the Inspiring Sermons section on the landing page.
        </p>
      </div>

      <AdminAlerts
        error={error}
        success={success}
        onClearError={() => setError(null)}
        onClearSuccess={() => setSuccess(null)}
      />

      <form onSubmit={handleSubmit} className="space-y-4">
        {sermons.length === 0 ? (
          <p className="text-secondary">No sermons yet. Add one below.</p>
        ) : (
          sermons.map((sermon, index) => (
            <div key={sermon.id ?? sermon.slug ?? `sermon-${index}`} className="card bg-base-100 shadow-xl">
              <div className="card-body gap-4 lg:flex-row">
                <div className="grid flex-1 gap-3 sm:grid-cols-2">
                  <label className="form-control">
                    <span className="label-text">Title</span>
                    <input
                      className="input input-bordered input-sm"
                      value={sermon.title}
                      onChange={(e) => updateSermon(index, { title: e.target.value })}
                    />
                  </label>
                  <label className="form-control sm:col-span-2">
                    <span className="label-text">YouTube embed URL</span>
                    <input
                      className="input input-bordered input-sm"
                      value={sermon.embedUrl}
                      onChange={(e) => updateSermon(index, { embedUrl: e.target.value })}
                      placeholder="https://www.youtube.com/embed/..."
                    />
                  </label>
                  <label className="label cursor-pointer justify-start gap-3 sm:col-span-2">
                    <input
                      type="checkbox"
                      className="checkbox checkbox-primary"
                      checked={sermon.enabled !== false}
                      onChange={(e) => updateSermon(index, { enabled: e.target.checked })}
                    />
                    <span className="label-text">Show on landing page</span>
                  </label>
                </div>

                <div className="flex shrink-0 flex-col gap-2">
                  <span className="text-xs font-semibold uppercase tracking-wide text-secondary">
                    Sermon {index + 1}
                  </span>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    disabled={index === 0}
                    onClick={() => {
                      setSermons((current) => {
                        const next = [...current]
                        ;[next[index - 1], next[index]] = [next[index], next[index - 1]]
                        return next
                      })
                    }}
                  >
                    Move up
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    disabled={index === sermons.length - 1}
                    onClick={() => {
                      setSermons((current) => {
                        const next = [...current]
                        ;[next[index], next[index + 1]] = [next[index + 1], next[index]]
                        return next
                      })
                    }}
                  >
                    Move down
                  </button>
                  <button
                    type="button"
                    className="btn btn-error btn-sm"
                    onClick={() => removeSermon(index)}
                  >
                    Remove
                  </button>
                </div>
              </div>
            </div>
          ))
        )}

        <div className="flex flex-wrap justify-between gap-3">
          <button type="button" className="btn btn-ghost" onClick={addSermon}>
            Add sermon
          </button>
          <button type="submit" className="btn btn-primary" disabled={saving || !isDirty}>
            {saving ? "Saving…" : "Save Sermons"}
          </button>
        </div>
      </form>
    </div>
  )
}

export default AdminSermons
